import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Currency } from 'domains-billing-currency';
import { graphQLToCurrency } from 'domains-billing-web-mappers';

import {
  TaxDetails,
  convertTaxAmountTypeEnum,
} from 'lane-shared/domains/billingPayments/types';
import { DEFAULT_TAX_DETAILS } from 'lane-shared/domains/billingPayments/constants';
import { convertToUUID } from 'uuid-encoding';

import {
  createBillingPaymentSettings,
  updateBillingPaymentSettings,
  getBillingPaymentSettings,
} from './helpers';

const DEFAULT_CURRENCY = Currency.USD;
const DEFAULT_DAYS = 30;

export type HookOptions = {
  channelId: string | undefined;
};

export function useBillingPaymentSettingsForChannel({
  channelId,
}: HookOptions) {
  const [error, setError] = useState<Error | null>(null);
  const [settingsUnchanged, setSettingsUnchanged] = useState<boolean>(true);

  const [taxDetails, setTaxDetails] = useState<TaxDetails>(DEFAULT_TAX_DETAILS);
  const [currency, setCurrency] = useState<Currency>(DEFAULT_CURRENCY);
  const [invoiceDueInDays, setInvoiceDueInDays] =
    useState<number>(DEFAULT_DAYS);

  const { t } = useTranslation();
  const defaultInvoiceDisplayName = t('abp.invoice.details.heading');

  const [invoiceDisplayName, setInvoiceDisplayName] = useState<string>(
    defaultInvoiceDisplayName
  );

  const [isInvoicingDisabled, setIsInvoicingDisabled] = useState(false);
  const [failureRefund, setFailureRefund] = useState<boolean>(false);
  const [cancellationRefund, setCancellationRefund] = useState<boolean>(false);

  function getSettings() {
    return {
      taxDetails,
      currency,
      invoiceDueInDays,
      invoiceDisplayName,
      invoicingDisabled: isInvoicingDisabled,
      refund: {
        failureRefund,
        cancellationRefund,
      },
    };
  }

  async function updatePaymentSettings(): Promise<boolean> {
    if (!channelId) {
      setError(new Error('abp.errors.generic.channelIdMissing'));

      return false;
    }

    let channelUUID;

    try {
      channelUUID = convertToUUID(channelId);
    } catch (err: any) {
      console.error(
        `BPError - updatePaymentSettings convertToUUID error: ${err}`
      );
      setError(new Error('abp.errors.generic.channelIdInvalid'));

      return false;
    }

    try {
      await updateBillingPaymentSettings(channelUUID, getSettings());

      setSettingsUnchanged(true);

      return true;
    } catch (err: any) {
      console.error(`BPError - updateBillingPaymentSettings error: ${err}`);
      setError(new Error(err.message));
    }

    return false;
  }

  function updateTaxDetails(value: TaxDetails) {
    setTaxDetails(value);
    setSettingsUnchanged(false);
  }

  function updateCurrency(value: Currency) {
    setCurrency(value);
    setSettingsUnchanged(false);
  }

  function updateInvoiceDueInDays(value: number | null) {
    setInvoiceDueInDays(value ?? DEFAULT_DAYS);
    setSettingsUnchanged(false);
  }

  function updateInvoiceDisplayName(value: string) {
    setInvoiceDisplayName(value);
    setSettingsUnchanged(false);
  }

  function updateIsInvoicingDisabled(value: boolean) {
    setIsInvoicingDisabled(value);
    setSettingsUnchanged(false);
  }

  function updateFailureRefund(value: boolean) {
    setFailureRefund(value);
    setSettingsUnchanged(false);
  }

  function updateCancellationRefund(value: boolean) {
    setCancellationRefund(value);
    setSettingsUnchanged(false);
  }

  async function getPaymentSettings() {
    let channelUUID;

    try {
      channelUUID = convertToUUID(channelId);
    } catch (err: any) {
      console.error(`BPError - getPaymentSettings convertToUUID error: ${err}`);
      setError(new Error('abp.errors.generic.channelIdInvalid'));

      return;
    }

    try {
      let channelSettings = await getBillingPaymentSettings(channelUUID);

      if (!channelSettings?.groupSettingId) {
        channelSettings = await createBillingPaymentSettings(
          channelUUID,
          getSettings()
        );
      }

      setTaxDetails({
        ...DEFAULT_TAX_DETAILS,
        ...channelSettings?.settings?.taxDetails,
        taxAmountType: convertTaxAmountTypeEnum(
          channelSettings?.settings?.taxDetails?.taxAmountType,
          DEFAULT_TAX_DETAILS.taxAmountType
        ),
      });

      const mappedCurrency = graphQLToCurrency(
        channelSettings?.settings?.currency
      );

      setCurrency(
        mappedCurrency === Currency.UNKNOWN ? DEFAULT_CURRENCY : mappedCurrency
      );
      setInvoiceDueInDays(channelSettings?.settings?.invoiceDueInDays);
      setInvoiceDisplayName(
        channelSettings?.settings?.invoiceDisplayName ||
          defaultInvoiceDisplayName
      );
      setIsInvoicingDisabled(channelSettings?.settings?.invoicingDisabled);

      setFailureRefund(
        channelSettings?.settings?.refund?.failureRefund || false
      );
      setCancellationRefund(
        channelSettings?.settings?.refund?.cancellationRefund || false
      );
    } catch (err: any) {
      if (err instanceof Error) {
        console.error(`BPError - getBillingPaymentSettings error: ${err}`);
        setError(new Error(err.message));
      } else {
        console.error(
          `BPError - getBillingPaymentSettings non-standard error: ${{
            ...err,
          }}`
        );
        setError(new Error({ ...err }));
      }
    }
  }

  useEffect(() => {
    if (channelId) {
      getPaymentSettings();
    }
  }, [channelId]);

  return {
    paymentSettingsError: error,
    currency,
    updateCurrency,
    taxDetails,
    updateTaxDetails,
    invoiceDueInDays,
    updateInvoiceDueInDays,
    settingsUnchanged,
    updatePaymentSettings,
    invoiceDisplayName,
    updateInvoiceDisplayName,
    isInvoicingDisabled,
    updateIsInvoicingDisabled,
    failureRefund,
    cancellationRefund,
    updateFailureRefund,
    updateCancellationRefund,
  } as const;
}
